"use client";

import { useHydrateBanks } from "@tillix/tx-core/react";
import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@tillix/ui-atoms/input-group";
import { formatRouting } from "@tillix/utils";
import { Controller, useFormContext } from "react-hook-form";
import { useT } from "@/features/i18n/hooks/use-t";
import { useBankByRoutingNumber } from "../hooks/useBankByRoutingNumber";
import { BankBadge } from "./BankBadge";

export interface RoutingNumberInputProps {
  name?: string;
  id: string;
}

export function RoutingNumberInput({
  name = "routingNumber",
  id,
}: RoutingNumberInputProps) {
  const { control } = useFormContext();
  const { t } = useT();
  const { t: tInputs } = useT("inputs");
  useHydrateBanks();
  const { bank, isLoading } = useBankByRoutingNumber(name);

  const showAddon = isLoading || bank !== null;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FieldRoot name={field.name}>
          <FieldLabel htmlFor={id}>{t("paymentForm.routingNumber")}</FieldLabel>
          <FieldControl
            render={(defaultProps) => (
              <InputGroup>
                <InputGroupInput
                  {...defaultProps}
                  autoComplete="off"
                  id={id}
                  inputMode="numeric"
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={(e) =>
                    field.onChange(formatRouting(e.target.value))
                  }
                  placeholder={t("paymentForm.routingNumberPlaceholder")}
                  ref={field.ref}
                  value={field.value as string}
                />
                {showAddon && (
                  <InputGroupAddon align="inline-end">
                    <BankBadge bank={bank} isLoading={isLoading} />
                  </InputGroupAddon>
                )}
              </InputGroup>
            )}
          />
          <FieldError>
            {fieldState.error
              ? tInputs("validation.invalidRoutingNumber")
              : undefined}
          </FieldError>
        </FieldRoot>
      )}
    />
  );
}
